import { Colors, Heading, Text } from '@amelio/ui-library';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';

const SKELETON_ROW_COUNT = 9;

type TemplatePreviewListProps = {
    heading: string;
    /** What the template holds, in its order: its questions, or the subjects of a plan. */
    items: string[];
    emptyMessage: string;
    isLoading?: boolean;
};

/** The numbered content of an Amelio template, as its preview shows it before it is imported. */
export const TemplatePreviewList = ({
    heading,
    items,
    emptyMessage,
    isLoading = false,
}: TemplatePreviewListProps) => (
    <>
        <Heading
            level={5}
            className={'mb-4 font-semibold tracking-tight'}
        >
            {heading}
        </Heading>

        {isLoading && (
            <div className={'space-y-3'}>
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                    <Skeleton key={index}>
                        {/* Height of a row: `p-4` on both sides of the `h-9` badge. */}
                        <Bone
                            className={'h-17 w-full'}
                            color={Colors.BASE_100}
                        />
                    </Skeleton>
                ))}
            </div>
        )}

        {!isLoading && items.length === 0 && <Text color={Colors.BASE_600}>{emptyMessage}</Text>}

        {!isLoading && items.length > 0 && (
            <ol className={'space-y-3'}>
                {items.map((item, index) => (
                    <li
                        key={index}
                        className={'flex items-center gap-4 p-4'}
                    >
                        <span
                            aria-hidden
                            className={
                                'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold'
                            }
                            style={{
                                background: `linear-gradient(135deg, ${Colors.SECONDARY_50}, ${Colors.SECONDARY_400})`,
                                color: Colors.SECONDARY_700,
                            }}
                        >
                            {index + 1}
                        </span>

                        <Text
                            className={'flex-1 leading-relaxed'}
                            weight={'medium'}
                            color={Colors.BASE_600}
                        >
                            {item}
                        </Text>
                    </li>
                ))}
            </ol>
        )}
    </>
);
